import { PrintTechPixelStats, PrintTechSignals } from './types';

// A colour bucket must hold at least this share of opaque pixels to count towards colourCount.
const MIN_COLOR_SHARE = 0.005;

export function emptyPixelStats(): PrintTechPixelStats {
  return {
    sampledPixels: 0,
    visiblePixels: 0,
    opaquePixels: 0,
    strongOpaquePixels: 0,
    semiTransparentPixels: 0,
    smoothGradientPixels: 0,
    strongEdgePixels: 0,
    colorBucketCounts: {},
  };
}

/** Merge `source` pixel stats into `target` (mutates and returns `target`). */
export function mergePixelStats(target: PrintTechPixelStats, source: PrintTechPixelStats): PrintTechPixelStats {
  target.sampledPixels += source.sampledPixels;
  target.visiblePixels += source.visiblePixels;
  target.opaquePixels += source.opaquePixels;
  target.strongOpaquePixels += source.strongOpaquePixels;
  target.semiTransparentPixels += source.semiTransparentPixels;
  target.smoothGradientPixels += source.smoothGradientPixels;
  target.strongEdgePixels += source.strongEdgePixels;

  Object.keys(source.colorBucketCounts).forEach((key) => {
    const numericKey = Number(key);
    target.colorBucketCounts[numericKey] =
      (target.colorBucketCounts[numericKey] || 0) + source.colorBucketCounts[numericKey];
  });

  return target;
}

/**
 * Derive the final per-print-area signals from aggregated pixel stats plus a
 * geometry-derived coverage estimate (0..1).
 */
export function deriveSignals(stats: PrintTechPixelStats, coverage: number): PrintTechSignals {
  const opaque = stats.opaquePixels || 1;
  const visible = stats.visiblePixels || 1;

  const colorCount = Object.values(stats.colorBucketCounts).filter((count) => count / opaque >= MIN_COLOR_SHARE).length;

  return {
    colorCount,
    gradientRatio: stats.smoothGradientPixels / opaque,
    edgeDensity: stats.strongEdgePixels / opaque,
    coverage: Math.max(0, Math.min(1, coverage)),
    semiTransparentRatio: stats.semiTransparentPixels / visible,
  };
}
